import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import { useFormatCurrency } from '../../hoc/useFormatCurrency';
import { requestRacesLeaderboard, resetRacesLeaderboard } from '../../store/actionCreators/racesLeaderboard';
import {
  getCurrentRace,
  getRacePointsUpdated,
  getRacesAccountResult,
  getSelectedGameBoxTag,
} from '../../store/selectors';
import LeaderBoard from './components/LeaderBoard';
import PrizePool from './components/PrizePool';
import Timer from './components/Timer';
import './styles.scss';

const tabs = ['leaderboard', 'prizePool'];

interface RaceProps {
  isLandscape: boolean;
}

const Race: FC<RaceProps> = ({ isLandscape }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const formatCurrency = useFormatCurrency();

  const selectedGbTab = useSelector(getSelectedGameBoxTag);
  const currentRace = useSelector(getCurrentRace);
  const accountResult = useSelector(getRacesAccountResult);
  const racePointsUpdated = useSelector(getRacePointsUpdated);
  const [isSpinning, setIsSpinning] = useState<boolean>(false);
  const [rankData, setRankData] = useState<Record<string, string>>({});
  const [selectedTab, selectTab] = useState<string>(tabs[0]);
  const [selectedRaceTab, setSelectedRaceTab] = useState<Record<string, string | number>>(
      currentRace[0] || currentRace
  );
  const [offset, setOffset] = useState<number>(0);
  const limit = 40;

  const classes = cn('race-tab', {
    'race-tab--mobile': isMobile,
    'race-tab--landscape': isLandscape && isMobile,
  });

  useEffect(() => {
    if (racePointsUpdated?.length > 0) {
      const found = racePointsUpdated?.find((element: { id: string | number }) => element.id === selectedRaceTab?.id);
      setRankData(found);
    }
  }, [racePointsUpdated, selectedRaceTab?.id]);

  useEffect(() => {
    if (selectedRaceTab?.id) {
      localStorage.setItem('raceId', String(selectedRaceTab?.id));
      dispatch(requestRacesLeaderboard(limit, 0, +selectedRaceTab?.id));
    }
  }, [selectedRaceTab?.id]);

  const setRaceTab = (index: number) => {
    setOffset(0);
    dispatch(resetRacesLeaderboard());
    setSelectedRaceTab(currentRace[index]);
  };

  const handleClick = () => {
    setIsSpinning(true);
    setOffset(0);
    dispatch(resetRacesLeaderboard());
    dispatch(requestRacesLeaderboard(limit, 0, +selectedRaceTab?.id));
    setTimeout(() => setIsSpinning(false), 1000);
  };

  if (selectedGbTab !== 'race') return null;

  return (Array.isArray(currentRace) && currentRace?.length > 0) || (Object.keys(currentRace)?.length) > 0 ? (
      <div className={classes}>
        {Array.isArray(currentRace) && currentRace?.length > 1 && (
            <div className="race-tab__switchers">
              {currentRace.map((race: any, index: number) => (
                  <div
                      key={index}
                      className={`race-tab__switchers__item ${selectedRaceTab.id === race?.id ? 'selected' : ''}`}
                      onClick={() => setRaceTab(index)}
                  >
                    <img src={race.img_url || `static/images/race/race-tab.png`} height={34} alt={race?.name} />
                    <p>
                      <span>{race?.name}</span>
                      <span>{formatCurrency(race?.prize_pool as number, race?.currency as string)}</span>
                    </p>
                  </div>
              ))}
            </div>
        )}
        <div className={cn('race-tab__content', { 'race-tab__change_content': Array.isArray(currentRace) && currentRace?.length > 1 })}>
          <div className="race-tab__name">
            <img src={(selectedRaceTab?.img_url as string) || `static/images/race/race-tab.png`} height={56} alt="race" />
            <p>
              <span>{selectedRaceTab?.name}</span>
              <p className="race-tab__id">#{selectedRaceTab?.id}</p>
            </p>
          </div>
          <div className="race-tab__prize">
            <img src={`static/images/race/coins.png`} width={50} alt="coins" />
            <p>{formatCurrency(selectedRaceTab?.prize_pool as number, selectedRaceTab?.currency as string)}</p>
          </div>
          <div className="race-tab__timer">
            <Timer selectedRaceTab={selectedRaceTab} />
          </div>
          <div className="race-tab__players">
            <ReactSVG src="/static/images/race/user.svg" width={8} />
            <p>{selectedRaceTab?.accountsCount}</p>
          </div>
          <div className="race-tab__table">
            <div className="tabs-switcher">
              {tabs.map((tab) => (
                  <div
                      className={`tabs-switcher__item ${selectedTab === tab ? 'selected' : ''}`}
                      onClick={() => selectTab(tab)}
                      key={tab}
                  >
                    {t(tab)}
                  </div>
              ))}
            </div>
            {accountResult?.rank || accountResult?.points || rankData?.points || rankData?.rank ? (
                <>
                  <div className="race__details">
                    <div className={cn('race__details__row', selectedTab + '__info')}>
                      <div className="race__position">{rankData?.rank || accountResult?.rank}</div>
                      <span>{t('you')}</span>
                      <img src={`static/images/race/fire.png`} width={24} alt="fire" />
                      <div className="race__score">{rankData?.points || accountResult?.points}</div>
                      <button style={{ marginLeft: '60px' }} onClick={handleClick}>
                        <img
                            src={`static/images/refresh.svg`}
                            width={14}
                            alt="refresh"
                            className={isSpinning ? 'refresh-spin' : ''}
                        />
                      </button>
                    </div>
                  </div>
                </>
            ) : (
                <div className="race__details">
                  <p className="race__details__emptyAccount">{t('emptyAccountResult')}</p>
                </div>
            )}
            {selectedTab === tabs[0] ? (
                <LeaderBoard selectedRaceTab={selectedRaceTab} offset={offset} setOffset={setOffset} />
            ) : (
                <PrizePool selectedRaceTab={selectedRaceTab} />
            )}
          </div>
        </div>
      </div>
  ) : (
      <></>
  );
};

export default Race;
